"use client"

import React, { useState, useEffect } from 'react';

import { useAuth } from '@clerk/nextjs'
import Link from 'next/link';

import useProjectStore from "@/store/projectStore";
import useUserStore from '@/store/userStore';
import useStore from '@/store/store';

import { LipSyncModel } from "@/app/enum";
import { getPlanBySlug } from "@/app/enum-subscription";
import { markPaymentIntentForReplay } from "@/utils/sentryReplay";

import { ChevronDown, Info } from "lucide-react"


const Step3Confirm = () => {

    const { userPlan } = useUserStore();
    const currentPlan = getPlanBySlug(userPlan);

    const hasAccessForAIModel = currentPlan.features.multiple_lip_sync_models;
    const hasAccessForLoopMode = currentPlan.features.select_video_loop_mode;
    const hasAccessForPrivateVisibility = currentPlan.features.private_video_visibility;

    const { genStatus, projVideo, projAudio, creditsNeeded, setCreditsNeeded, set_AiModel, set_AiLoopMode, set_isPublic } = useProjectStore();
    const { userCredits } = useUserStore();
    const { openPricingModal } = useStore();

    const [selectedModelId, setSelectedModelId] = useState("latentsync");
    const [selectedModel, setSelectedModel] = useState(LipSyncModel[selectedModelId]);
    const [selectedLoopMode, setSelectedLoopMode] = useState("pingpong");
    const [isPublicVisibility, setIsPublicVisibility] = useState(true);

    const [basedOn, setBasedOn] = useState("video");


    const onChangeModelId = (modelId) => {
        setSelectedModelId(modelId)
        setSelectedModel(LipSyncModel[modelId])
        setSelectedLoopMode(LipSyncModel[modelId].loop.defaultLoopMode)

        // store
        set_AiModel(modelId)
        set_AiLoopMode(LipSyncModel[modelId].loop.defaultLoopMode)
    }

    const onChangeLoopMode = (loopMode) => {
        setSelectedLoopMode(loopMode)

        // store
        set_AiLoopMode(loopMode);
    }

    const onChangePublicVisibility = (e) => {
        const newValue = e.target.checked;

        // 如果用户尝试取消勾选（即想要设置为私密）
        if (!newValue) {

            // 检查用户是否有私密可见性权限
            if (!hasAccessForPrivateVisibility) {
                // 没有权限，弹出提示并阻止取消勾选
                markPaymentIntentForReplay("private_visibility_upgrade_prompt", {
                    currentPlan: userPlan || "free"
                });
                openPricingModal({
                    title: "Private Project Needed",
                    description: "Upgrade your plan to set your projects as private. Keep your creations exclusive and protect your privacy."
                });
                return;
            }
        }

        // 允许更改
        setIsPublicVisibility(newValue)
        set_isPublic(newValue)
    }

    useEffect(() => {
        // 计算 creditsCost：每 30 秒消耗 require_credits
        const videoDuration = projVideo.duration || 0;
        const audioDuration = projAudio.duration || 0;

        // 谁长用谁，选择 videoDuration 和 audioDuration 中较大的一个
        const duration = Math.max(videoDuration, audioDuration);

        setBasedOn(duration === videoDuration ? "video" : "audio");

        const creditsPerUnit = LipSyncModel[selectedModelId].require_credits;
        const units = Math.ceil(duration);

        // let units = Math.ceil(duration / 10);
        // if (duration > 0 && duration < 10) {
        //     units = 1;
        // }

        setCreditsNeeded(units * creditsPerUnit);
    }, [selectedModelId]);


    return (
        <div className="">
            {genStatus == 0 && (
                <div className='md:p-4'>
                    <div className="mt-4">
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <div className="mt-4 md:p-4 rounded-lg hidden">
                                <div className="mb-4 text-sm text-center text-base-content/65">Video</div>
                                <div className="flex justify-center items-center bg-base-200 rounded-lg h-32">
                                    <video src={projVideo.source} style={{ height: '100px' }}></video>
                                </div>
                            </div>

                            <div className="mt-4 md:p-4 rounded-lg hidden">
                                <div className="mb-4 text-sm text-center text-base-content/65">Audio</div>
                                <div className="flex justify-center items-center bg-base-200 rounded-lg h-32">
                                    <audio src={projAudio.source} controls></audio>
                                </div>
                            </div>

                            {/* 模型选择 */}
                            <div className=''>
                                <fieldset className="fieldset bg-base-200 border-base-300 rounded-box border p-4">
                                    <legend className="fieldset-legend">Lip Sync Model</legend>

                                    <div className="dropdown">
                                        <div tabIndex={0} role="button" className="p-4 rounded-box bg-base-100 border border-base-300 hover:border-white/50 transition-all cursor-pointer text-base flex items-center justify-between">
                                            <div className='flex items-center gap-2'>
                                                <img src={`https://rs.magicface.co/indiesites/lipsync-show/app/aimodel-icon-${selectedModel.id}.png`} alt="" className='w-10 h-10 rounded-full' />
                                                {selectedModel.name}
                                            </div>
                                            <ChevronDown className="w-4 h-4" />
                                        </div>
                                        <ul tabIndex={0} className="dropdown-content bg-base-200 rounded-box border border-base-300 z-1 w-full p-2 shadow-sm">
                                            {
                                                Object.values(LipSyncModel).map((model) => (
                                                    <li
                                                        className='px-2 py-4 rounded-box hover:bg-base-100 transition-all cursor-pointer flex items-center justify-between'
                                                        key={model.name}
                                                        onClick={() => onChangeModelId(model.id)}
                                                    >
                                                        <div className='flex items-center gap-2'>
                                                            <div>
                                                                <img src={`https://rs.magicface.co/indiesites/lipsync-show/app/aimodel-icon-${model.id}.png`} alt="" className='w-10 h-10 rounded-full' />
                                                            </div>

                                                            <div>
                                                                <div className='text-base flex items-center gap-2'>{model.name} {model.require_subs && <img src="/diamond.svg" alt="" className='w-4 h-4' />}</div>
                                                                <div className='text-base-content/65'>{model.info}</div>
                                                            </div>
                                                        </div>

                                                        <div className='flex items-center gap-2 text-lg'>
                                                            <img src="/credits.svg" alt="" className='w-4 h-4' />
                                                            {model.require_credits}
                                                            <span className='text-sm text-base-content/65'>/ min</span>
                                                        </div>
                                                    </li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </fieldset>
                            </div>

                            {/* 循环模式选择 */}
                            <div className=''>
                                <fieldset className="fieldset bg-base-200 border-base-300 rounded-box border p-4 relative">
                                    {
                                        !hasAccessForLoopMode && (
                                            <div className='w-full h-full bg-base-100 absolute top-0 left-0 z-10 flex justify-center items-center gap-2' style={{ backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0, 0, 0, 0.55)' }}>
                                                <img src="/locked.svg" alt="" className='w-4 h-4' />
                                                <span className='text-amber-200'>Upgrade to Premium to select video loop mode</span>
                                            </div>
                                        )
                                    }

                                    <legend className="fieldset-legend">Loop mode</legend>
                                    {
                                        LipSyncModel[selectedModelId] && LipSyncModel[selectedModelId].loop.support ? (
                                            <div className="grid grid-cols-3 gap-2 my-2">
                                                {
                                                    LipSyncModel[selectedModelId].loop.loopModes.map((loopMode) => (
                                                        <input
                                                            className="join-item btn"
                                                            type="radio"
                                                            name="options"
                                                            aria-label={loopMode}
                                                            key={loopMode}
                                                            value={loopMode}
                                                            onClick={() => onChangeLoopMode(loopMode)}
                                                            defaultChecked={selectedLoopMode == loopMode}
                                                        />
                                                    ))
                                                }
                                            </div>
                                        ) : (
                                            <div className="text-base-content text-center text-base">
                                                {selectedModelId === "latentsync"
                                                    ? "LatentSync does not support loop mode."
                                                    : "Loop mode is not supported."
                                                }
                                                {LipSyncModel[selectedModelId].loop.info && (
                                                    <div className="text-base-content/70 text-sm mt-2 mx-auto w-2/3">{LipSyncModel[selectedModelId].loop.info}</div>
                                                )}
                                            </div>
                                        )
                                    }
                                </fieldset>
                            </div>

                            {/* 视频时长展示 */}
                            <div className="mt-4 py-4 px-8 rounded-lg bg-base-200 flex justify-around items-center">
                                <div>
                                    <div className="mb-1 text-center text-base-content">Video Duration:</div>
                                    <div className="flex items-center justify-center">
                                        <div className="flex items-center">
                                            <span className="text-xl text-info">{parseFloat(projVideo.duration).toFixed(0)}</span>
                                            <span className="ml-2 text-sm text-info/65">Seconds</span>
                                        </div>
                                    </div>
                                </div>
                                <div className='divider divider-horizontal'></div>
                                <div>
                                    <div className="mb-1 text-center text-base-content">Audio Duration:</div>
                                    <div className="flex items-center justify-center">
                                        <div className="flex items-center">
                                            <span className="text-xl text-info">{parseFloat(projAudio.duration).toFixed(0)}</span>
                                            <span className="ml-2 text-sm text-info/65">Seconds</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 积分消耗展示 */}
                            <div className="mt-4 p-4 rounded-lg bg-amber-500/10">
                                <div className="mb-1 text-center text-base-content">
                                    Credits Needed, based on
                                    <div className="tooltip" data-tip="The credits needed is based on the longer duration between video and audio.">
                                        <span className="ml-1 text-base-content font-bold text-warning border-b border-dashed border-warning cursor-help">{basedOn == "video" ? "video duration" : "audio duration"}</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <div className="flex items-center">
                                        <span className="text-xl text-warning">{creditsNeeded}</span>
                                        <span className="ml-2 text-sm text-warning/65">Credits</span>

                                        {creditsNeeded > userCredits && (
                                            <Link
                                                href="/subscription"
                                                className="ml-2 text-sm text-error underline"
                                                onClick={() => markPaymentIntentForReplay("insufficient_credits_link_clicked", {
                                                    creditsNeeded,
                                                    userCredits,
                                                    currentPlan: userPlan || "free"
                                                })}
                                            >( You need to recharge )</Link>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 提示用户作品不受保护 */}
                            <div className="">
                                <fieldset className="fieldset bg-base-100 border-base-300 rounded-box border p-4">
                                    <legend className="fieldset-legend">Public Visibility</legend>
                                    <label className="label justify-between">
                                        <div className='flex items-center gap-2'>
                                            <img src="/diamond.svg" alt="" className='w-4 h-4' />
                                            <span className="text-sm text-base-content">Public to community</span>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            <div className="tooltip" data-tip="The output video may be selected by LipSyncShow and published to the showcase feed.">
                                                <Info className="w-4 h-4 mr-2 cursor-help" />
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={isPublicVisibility}
                                                onChange={onChangePublicVisibility}
                                                className="toggle"
                                            />
                                        </div>
                                    </label>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {genStatus === 1 && (
                <div className="flex justify-center items-center" style={{ height: '40vh' }}>
                    <div className="flex flex-col justify-center items-center">
                        <div className="text-2xl mt-4">Lip Syncing ...</div>
                        <div className="text-neutral-content/65 text-center mt-2">You can safely leave this page</div>
                        <div className="text-neutral-content/65 text-center mt-2">Generation will be completed in 1~5 few minutes</div>
                    </div>
                </div>
            )}

            {/* {projectStore.status === 2 && (
                <div>
                    <video id="resultVideo" controls className="w-full rounded-lg">Your browser does not support the video tag.</video>

                    <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center">
                            <div className="text-sm">Time Cost:</div>
                            <div className="text-lg ml-2">seconds</div>
                        </div>

                        <div>
                            <a id="downloadBtn" className="btn btn-primary">Download</a>
                        </div>
                    </div>
                </div>
            )} */}
        </div>
    );
};

export default Step3Confirm;
